import React, { useMemo } from 'react'
import injectSheet from 'react-jss'
import Button from 'pobble-components/dist/button'
import ButtonElement from 'pobble-components/dist/button-element'
import {
  useSubscriptionDetails,
  useChargebeeScript
} from 'pobble-components/dist/subscriptions/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle'
import { ENV } from 'pobble-components/dist/setup'
import { useMe } from 'pobble-components/dist/pobble-user'

const setLinkEnv = link => {
  return `${link}${ENV !== 'prod' ? '-stg' : ''}`
}

const CTANote = injectSheet({
  note: {
    fontSize: '12px',
    margin: '-32px 0 0',
    color: 'var(--gray-)'
  }
})(({ classes, children }) => {
  return <p className={classes.note}>{children}</p>
})

const useHomeSubscription = () => {
  const {
    individualPlan,
    currentSubsType,
    cancelled
  } = useSubscriptionDetails()

  const hasHomePlan = individualPlan === 'pobble-home' && !cancelled

  return {
    isSubscribedAnnual: hasHomePlan && currentSubsType === 'annual',
    isSubscribedMonthly: hasHomePlan && currentSubsType === 'monthly'
  }
}

const TLHome = () => {
  const { prices, currencySign } = useSubscriptionDetails()
  const { openCheckout } = useChargebeeScript()
  const { isSubscribedAnnual } = useHomeSubscription()
  const monthlyEquivalent = Math.floor(
    parseFloat(prices.annual.pobble_home) / 12
  )

  return (
    <Tier>
      <Description>
        <h2>Yearly subscription</h2>
        <p>
          {currencySign}
          {prices.annual.pobble_home} a year. Works out at just over <br />
          {currencySign}
          {monthlyEquivalent} a month.
        </p>
      </Description>
      <Price
        currency={currencySign}
        value={prices.annual.pobble_home}
        note="billed annually, inc. VAT."
      />
      {isSubscribedAnnual ? (
        <Button big wide disabled>
          Subscribed
        </Button>
      ) : (
        <>
          <Button
            action={() => {
              openCheckout({
                url: 'v3/me/subscriptions/checkout_new_pobble_home_js',
                params: {
                  annual: true
                }
              })
            }}
            big
            wide>
            Get started
          </Button>
        </>
      )}

      <HomePerks />
    </Tier>
  )
}

const TLHomeMonthly = () => {
  const { prices, currencySign } = useSubscriptionDetails()
  const { openCheckout } = useChargebeeScript()
  const { isSubscribedMonthly } = useHomeSubscription()

  return (
    <Tier>
      <Description>
        <h2>Monthly subscription</h2>
        <p>Renews monthly unless you cancel.</p>
      </Description>
      <Price
        currency={currencySign}
        value={prices.monthly.pobble_home}
        period="per month"
        note="billed monthly, inc. VAT."
      />
      {isSubscribedMonthly ? (
        <Button big wide disabled>
          Subscribed
        </Button>
      ) : (
        <>
          <Button
            action={() => {
              openCheckout({
                url: 'v3/me/subscriptions/checkout_new_pobble_home_js',
                params: {
                  annual: false
                }
              })
            }}
            big
            wide>
            Get started
          </Button>
        </>
      )}

      <HomePerks />
    </Tier>
  )
}

const TLIndividual = ({ active }) => {
  const {
    prices,
    currencySign,
    offerFreeTrial,
    individualModeration
  } = useSubscriptionDetails()
  const { openCheckout } = useChargebeeScript()

  return (
    <Tier>
      <Description>
        <h2>Individual subscription</h2>
        <p>
          A single Pobble 365 subscription. Ideal for individual teachers or
          tutors looking to inspire their students with engaging writing
          resources.
        </p>
      </Description>
      <Price
        currency={currencySign}
        value={parseInt(prices.annual.pobble_silver)}
        note="billed annually, inc. VAT."
      />
      {active ? (
        <Button big wide disabled>
          Subscribed
        </Button>
      ) : (
        <>
          <Button
            action={() => {
              openCheckout({
                url: 'v3/me/subscriptions/checkout_new_pobble_silver_js',
                params: {
                  annual: true,
                  add_trial: offerFreeTrial,
                  moderation: individualModeration
                }
              })
            }}
            big
            wide>
            {offerFreeTrial && 'Start my 7-day free trial'}
            {!offerFreeTrial && 'Buy now'}
          </Button>
          <CTANote>An invoice will be generated at the checkout</CTANote>
        </>
      )}

      <List>
        <ListItem>
          Unlimited access to the entire Pobble 365 calendar, including
          differentiated lessons.
        </ListItem>
        <ListItem>Access to themed lessons, quick writes, and more.</ListItem>
        <ListItem>
          The ability to edit, save, and share unlimited content.
        </ListItem>
      </List>
    </Tier>
  )
}

const TLSchool = injectSheet({
  accounts: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '24px'
  },
  label: {
    display: 'block'
  },
  select: {
    background: '#fff'
  }
})(() => {
  const { prices, currencySign } = useSubscriptionDetails()
  return (
    <Tier>
      <Description>
        <h2>School subscription</h2>
        <p>
          Pobble 365 subscriptions for every teacher, billed centrally to your
          school. Ideal if you're looking to improve writing outcomes across all
          year groups.
        </p>
      </Description>
      <Price
        currency={currencySign}
        value={parseInt(prices.annual.pobble_school_a)}
        note="including VAT if applicable"
      />
      <Button
        as="a"
        _blank
        href={setLinkEnv(
          'https://static.pobble.com/get-school-pobble-subscription'
        )}
        big
        wide
        secondary>
        Sign up now
      </Button>
      <CTANote>
        Need your finance department to pay? Select 'Bank Transfer' at the
        checkout to generate an invoice.
      </CTANote>
      <div>
        <List>
          <ListItem noIcon>
            All the benefits of an Individual subscription, plus:
          </ListItem>
          <ListItem>
            Centralised billing (including VAT invoices, if applicable).
          </ListItem>
          <ListItem>
            A complimentary call to help you set up and launch Pobble in your
            school.
          </ListItem>
        </List>
      </div>
    </Tier>
  )
})

const TLGroup = () => {
  return (
    <Tier>
      <Description>
        <h2>Group subscription</h2>
        <p>
          Pobble 365 subscriptions for all staff members across multiple schools
          billed centrally.
        </p>
      </Description>
      <Button
        as="a"
        href="https://share.hsforms.com/1fB2BFTuzRWuCMQr3LGYmNw4oqv9"
        _blank
        big
        wide
        secondary>
        Enquire now
      </Button>
      <List>
        <ListItem noIcon>Additional benefits:</ListItem>
        <ListItem>
          Centralised billing (including VAT invoices, if applicable).
        </ListItem>
        <ListItem>
          Discounts available for groups of more than 4 schools.
        </ListItem>
        <ListItem>Set up support including MIS Integration.</ListItem>
        <ListItem>Bespoke training opportunities.</ListItem>
      </List>
    </Tier>
  )
}

{
  /*const ModerationIndividual = () => {
  const {
    prices,
    currencySign,
    individualModeration
  } = useSubscriptionDetails()

  const { openCheckout } = useChargebeeScript()

  return (
    <Tier>
      <Description>
        <h2>Individual subscription</h2>
        <p>A single moderation subscription for an individual teacher.</p>
      </Description>
      <Price
        currency={currencySign}
        value={parseInt(prices.annual.pobble_moderation)}
        note="billed annually, inc. VAT."
      />
      {individualModeration && (
        <Button big wide disabled>
          Subscribed
        </Button>
      )}
      {!individualModeration && (
        <Button
          onClick={() => {
            openCheckout({
              url: 'v3/me/subscriptions/checkout_moderation_addon_js',
              params: { annual: true }
            })
          }}
          big
          wide
          secondary>
          Buy now
        </Button>
      )}
      <List>
        <ListItem>Create and share moderation files.</ListItem>
        <ListItem>Access to cross-school moderation sessions.</ListItem>
        <ListItem>Access to writing samples created by Pobble.</ListItem>
      </List>
    </Tier>
  )
}*/
}

const ModerationSchool = () => {
  const { prices, currencySign } = useSubscriptionDetails()

  return (
    <Tier>
      <Description>
        <h2>School subscription</h2>
        <p>Unlimited Moderation subscriptions for your whole school team.</p>
      </Description>
      <Price
        currency={currencySign}
        value={parseInt(prices.annual.pobble_school_b)}
        note="including VAT if applicable"
      />
      <Button
        as="a"
        href={setLinkEnv(
          'https://static.pobble.com/get-school-moderation-subscription'
        )}
        _blank
        big
        wide>
        Buy now
      </Button>
      <List>
        <ListItem>
          Free training for your English/writing leader with a moderation
          expert.
        </ListItem>
        <ListItem>
          The ability to create your own school/MAT writing exemplars.
        </ListItem>
        <ListItem>
          Easy for teachers to collaborate on pupil and curriculum next steps.
        </ListItem>
        <ListItem>
          English leaders can identify whole-school CPD needs.
        </ListItem>
        <ListItem>
          The option to create files to use for in-school CPD.
        </ListItem>
      </List>
    </Tier>
  )
}

const ModerationGroup = () => {
  const { currencySign } = useSubscriptionDetails()

  return (
    <Tier>
      <Description>
        <h2>School group subscription</h2>
        <p>Moderation subscriptions across your whole school group.</p>
      </Description>
      <Price
        currency={currencySign}
        value={250}
        note="billed annually, excl. VAT"
      />
      <Button
        as="a"
        href="https://share.hsforms.com/1EPdHt_8MSbuB2nNFfFhcXA4oqv9"
        _blank
        big
        wide
        secondary>
        Get your quote
      </Button>
      <List>
        <ListItem>All the benefits of a school subscription.</ListItem>
        <ListItem>
          Centralised billing (including VAT invoices, if applicable).
        </ListItem>
        <ListItem>
          Discounts are available for groups of more than 4 schools.
        </ListItem>
        <ListItem>Set up support including MIS Integration.</ListItem>
        <ListItem>Bespoke training opportunities.</ListItem>
      </List>
    </Tier>
  )
}

const Tier = injectSheet({
  tier: {
    padding: '48px 24px',
    borderRadius: '16px',
    background: 'var(--light-gray--)',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    font: 'var(--body-)',
    color: 'var(--gray-)',
    gap: '48px',
    width: '100%',
    '@media (min-width: 768px)': {
      maxWidth: '320px'
    },
    '@media (min-width: 1024px)': {
      maxWidth: '380px'
    }
  }
})(({ classes, children }) => {
  return <div className={classes.tier}>{children}</div>
})

const Description = injectSheet({
  description: {
    '& h2': {
      margin: '0 0 24px',
      font: 'var(--heading-)',
      color: 'var(--gray)'
    },
    '& p': {
      margin: '16px 0'
    }
  }
})(({ classes, children }) => {
  return <div className={classes.description}>{children}</div>
})

const Price = injectSheet({
  container: {},
  price: {
    display: 'flex',
    font: 'var(--body)',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: '4px',
    margin: '0',
    color: 'var(--gray)'
  },
  value: {
    fontSize: '56px',
    lineHeight: '40px'
  },
  note: {
    margin: '8px 0 0',
    font: 'var(--body--)'
  }
})(({ classes, currency, value, note, period = 'per year' }) => {
  return (
    <div>
      <p className={classes.price}>
        <span>{currency}</span>
        <span className={classes.value}>{value}</span>
        <span>{period}</span>
      </p>
      {note && <p className={classes.note}>{note}</p>}
    </div>
  )
})

const List = injectSheet({
  list: {
    listStyle: 'none',
    padding: '0',
    margin: '0',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    gap: '16px'
  }
})(({ classes, children }) => {
  return <ul className={classes.list}>{children}</ul>
})

const ListItem = injectSheet({
  item: {
    display: 'flex',
    gap: '8px',
    '& > *': {
      flexShrink: '0'
    },
    '& svg': {
      color: 'var(--green)',
      marginTop: '2px'
    }
  },
  noIcon: {
    '& svg': {
      opacity: '0'
    }
  }
})(({ classes, children, noIcon }) => {
  return (
    <li className={`${classes.item} ${noIcon ? classes.noIcon : ''}`}>
      <FontAwesomeIcon icon={faCheckCircle} /> {children}
    </li>
  )
})

const HomePerks = () => (
  <List>
    <ListItem>Daily prompts</ListItem>
    <ListItem>A choice of three levels</ListItem>
    <ListItem>Printable writing packs</ListItem>
    <ListItem>A secure place to save everything your child writes</ListItem>
  </List>
)

const Tabs = injectSheet({
  tabs: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '64px -32px 32px',
    background: 'linear-gradient(180deg, transparent, #f1f1f1)',
    borderBottom: '1px solid #ddd',
    fontWeight: 'bold',
    fontSize: '14px',
    '@media (min-width: 768px)': {
      font: 'var(--heading-)'
    },
    '& button': {
      borderRadius: '8px 8px 0 0',
      padding: '16px 24px',
      overflow: 'hidden',
      background: '#fff',
      border: '1px solid #ddd',
      borderTopWidth: '6px',
      marginBottom: '-1px',
      position: 'relative',
      textAlign: 'center',
      '@media (min-width: 768px)': {
        width: '280px'
      },
      '&:first-child': {
        marginRight: '16px'
      },
      '&:after': {
        content: '""',
        position: 'absolute',
        bottom: '-1px',
        left: '0',
        width: '100%',
        height: '1px',
        boxShadow: '0 0 10px rgba(0, 0, 0, .5)'
      }
    }
  },
  activeTab: {
    borderBottomColor: '#fff !important',
    background: '#fff !important',
    borderTopColor: 'var(--blue) !important',
    '&:after': {
      display: 'none'
    }
  }
})(({ classes, activeTab, setActiveTab }) => {
  const { me } = useMe()
  const isParent = useMemo(() => me?.account?.role_in_life === 'parent', [me])

  return (
    <div className={classes.tabs}>
      {!isParent && (
        <ButtonElement
          onClick={() => setActiveTab('tl')}
          className={activeTab === 'tl' ? classes.activeTab : undefined}>
          Pobble 365
        </ButtonElement>
      )}
      {!isParent && (
        <ButtonElement
          onClick={() => setActiveTab('moderation')}
          className={
            activeTab === 'moderation' ? classes.activeTab : undefined
          }>
          Moderation
        </ButtonElement>
      )}
      {isParent && (
        <ButtonElement
          onClick={() => setActiveTab('parent')}
          className={activeTab === 'parent' ? classes.activeTab : undefined}>
          Pobble for Home
        </ButtonElement>
      )}
    </div>
  )
})

const SubscriptionTiers = injectSheet({
  tiers: {
    display: 'flex',
    gap: '24px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    font: 'var(--body)',
    color: 'var(--gray)',
    '& h2': {
      margin: '0 0 0',
      font: 'var(--heading_)',
      color: 'var(--dark-blue)'
    },
    '& p': {
      margin: '16px 0'
    }
  }
})(({ classes, activeTab = 'tl', setActiveTab, active }) => {
  return (
    <div className={classes.container}>
      <div className={classes.head}>
        <h2>
          {activeTab === 'parent'
            ? 'One simple plan, two ways to pay.'
            : 'Choose the right Premium subscription for you'}
        </h2>
        {activeTab === 'tl' && (
          <p>Engage and motivate your children to write!</p>
        )}
        {activeTab === 'moderation' && (
          <p>
            Improve your moderation process and be confident in your assessment
            judgements.
          </p>
        )}
        {activeTab === 'parent' && (
          <p>The spark your child needs to enjoy writing</p>
        )}
      </div>
      <div className={classes.container}>
        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
        <div className={classes.tiers}>
          {activeTab === 'tl' && (
            <>
              <TLIndividual
                active={
                  active === 'pobble-silver' ||
                  active === 'pobble_silver_plus_moderation'
                }
              />
              <TLSchool />
              <TLGroup />
            </>
          )}
          {activeTab === 'moderation' && (
            <>
              <ModerationSchool />
              <ModerationGroup />
            </>
          )}
          {activeTab === 'parent' && (
            <>
              <TLHomeMonthly />
              <TLHome />
            </>
          )}
        </div>
      </div>
    </div>
  )
})

export default SubscriptionTiers
